import { storage } from './storage';
import type { SearchResultItem } from '@services/searchService';

const HISTORY_KEYS = {
  PROJECT: 'C2_BeforeSearchObjectProject',
  DASHBOARD: 'C2_BeforeSearchObjectDashboard',
  PEOPLE: 'C2_BeforeSearchObjectPeople',
  IS_LAST: 'C2_IsLastBeforeSearch',
} as const;

const MAX_HISTORY = {
  PROJECT: 5,
  DASHBOARD: 8,
  PEOPLE: 6,
} as const;

function addToHistory(key: string, item: SearchResultItem, maxItems: number): void
{
  const items = storage.getJSON<SearchResultItem[]>(key) || [];
  const filtered = items.filter(existing => existing.ID !== item.ID);
  filtered.unshift(item);
  if (filtered.length > maxItems)
  {
    filtered.length = maxItems;
  }
  storage.setJSON(key, filtered);
}

export const searchHistory = {
  getSearchHistory(): SearchResultItem[]
  {
    const projects = (storage.getJSON<SearchResultItem[]>(HISTORY_KEYS.PROJECT) || []).slice(
      0,
      MAX_HISTORY.PROJECT,
    );
    const dashboards = (storage.getJSON<SearchResultItem[]>(HISTORY_KEYS.DASHBOARD) || []).slice(
      0,
      MAX_HISTORY.DASHBOARD,
    );
    const people = (storage.getJSON<SearchResultItem[]>(HISTORY_KEYS.PEOPLE) || []).slice(
      0,
      MAX_HISTORY.PEOPLE,
    );

    const result: SearchResultItem[] = [];

    if (projects.length > 0)
    {
      result.push({
        ID: 0,
        Name: 'Projects',
        ImageName: null,
        isOnLine: false,
        Type: 'Header',
        TypeId: 0,
        Icon: '',
        ProjectId: null,
        ProjectName: null,
        IsDashboardsFilter: false,
      });
      result.push(...projects);
    }

    if (dashboards.length > 0)
    {
      result.push({
        ID: 0,
        Name: 'Dashboards',
        ImageName: null,
        isOnLine: false,
        Type: 'Header',
        TypeId: 0,
        Icon: '',
        ProjectId: null,
        ProjectName: null,
        IsDashboardsFilter: false,
      });
      result.push(...dashboards);
    }

    if (people.length > 0)
    {
      result.push({
        ID: 0,
        Name: 'People',
        ImageName: null,
        isOnLine: false,
        Type: 'Header',
        TypeId: 0,
        Icon: '',
        ProjectId: null,
        ProjectName: null,
        IsDashboardsFilter: false,
      });
      result.push(...people);
    }

    return result;
  },

  addProjectToHistory(item: SearchResultItem): void
  {
    addToHistory(HISTORY_KEYS.PROJECT, item, MAX_HISTORY.PROJECT);
  },

  addDashboardToHistory(item: SearchResultItem): void
  {
    addToHistory(HISTORY_KEYS.DASHBOARD, item, MAX_HISTORY.DASHBOARD);
  },

  addPersonToHistory(item: SearchResultItem): void
  {
    addToHistory(HISTORY_KEYS.PEOPLE, item, MAX_HISTORY.PEOPLE);
  },

  /** Save a project visit from page-level data */
  addProjectVisit(projectId: number, projectName: string): void
  {
    const item: SearchResultItem = {
      ID: projectId,
      Name: projectName,
      ImageName: null,
      isOnLine: false,
      Type: 'Project',
      TypeId: 9,
      Icon: 'fa fa-folder',
      ProjectId: null,
      ProjectName: null,
      IsDashboardsFilter: false,
    };
    addToHistory(HISTORY_KEYS.PROJECT, item, MAX_HISTORY.PROJECT);
  },

  /** Save a dashboard visit from page-level data */
  addDashboardVisit(
    dashboardId: number,
    dashboardName: string,
    projectId?: number | null,
    projectName?: string | null,
  ): void
  {
    const item: SearchResultItem = {
      ID: dashboardId,
      Name: dashboardName,
      ImageName: null,
      isOnLine: false,
      Type: 'Dashboard',
      TypeId: 1,
      Icon: 'fa fa-columns',
      ProjectId: projectId ?? null,
      ProjectName: projectName ?? null,
      IsDashboardsFilter: true,
    };
    addToHistory(HISTORY_KEYS.DASHBOARD, item, MAX_HISTORY.DASHBOARD);
  },

  /** Save a person visit from page-level data */
  addPersonVisit(personId: number, personName: string, imageName?: string | null): void
  {
    const item: SearchResultItem = {
      ID: personId,
      Name: personName,
      ImageName: imageName ?? null,
      isOnLine: false,
      Type: 'Person',
      TypeId: 15,
      Icon: 'fa fa-user',
      ProjectId: null,
      ProjectName: null,
      IsDashboardsFilter: false,
    };
    addToHistory(HISTORY_KEYS.PEOPLE, item, MAX_HISTORY.PEOPLE);
  },

  resetLastSearch(): void
  {
    storage.setItem(HISTORY_KEYS.IS_LAST, '0');
  },
};
